import { useMemo, useRef, useSyncExternalStore } from 'react';

export interface Store<T> {
  getState: () => T;
  setState: (next: T | ((previous: T) => T)) => void;
  subscribe: (listener: () => void) => () => void;
  reset: () => void;
}

export const createStore = <T>(initialState: T): Store<T> => {
  let state = initialState;
  const listeners = new Set<() => void>();
  return {
    getState: () => state,
    setState: next => {
      const resolved = typeof next === 'function' ? (next as (previous: T) => T)(state) : next;
      if (Object.is(resolved, state)) return;
      state = resolved;
      [...listeners].forEach(listener => listener());
    },
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    reset: () => {
      state = initialState;
      listeners.clear();
    },
  };
};

const isPlainRecord = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

export const structuralEqual = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => structuralEqual(item, b[index]));
  }
  if (!isPlainRecord(a) || !isPlainRecord(b)) return false;
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every(key => Object.prototype.hasOwnProperty.call(b, key) && structuralEqual(a[key], b[key]))
  );
};

export const useStore = <T, S = T>(
  store: Store<T>,
  selector?: (state: T) => S,
  isEqual: (a: S, b: S) => boolean = structuralEqual,
): S => {
  const cache = useRef<{ value: S } | null>(null);
  const getSnapshot = useMemo(() => {
    if (!selector) return store.getState as unknown as () => S;
    return () => {
      const next = selector(store.getState());
      const previous = cache.current;
      if (previous && isEqual(previous.value, next)) return previous.value;
      cache.current = { value: next };
      return next;
    };
  }, [store, selector, isEqual]);
  return useSyncExternalStore(store.subscribe, getSnapshot);
};
